import React, { useRef, useState } from 'react'

import { Button, FormattedHTMLMessage, Input } from '@web/walter-ui-kit'
import cn from 'classnames'
import { useIntl } from 'react-intl'
import { GetCard } from '@web/walter-lib/api'
import s from './s.module.css'
import { lang } from './lang'
import { cardsApi, useGetThreeDSConfigData, useGetThreeDSQuestionsData } from '~api'
import { usePromise } from '~hooks/usePromise'
import { SettingsTabs } from '~pages/CardsPage/Cards/Settings/SettingsModal/types'

interface Props {
    onClose?: () => void
    setTab: (tab: SettingsTabs) => void
    card?: GetCard
}

function ProgressBar({ questionLen, curQuestion }: { questionLen: number; curQuestion: number }) {
    return (
        <div className={s.progressBar}>
            {Array(questionLen)
                .fill(0)
                .map((_, i) => (
                    <div key={i} className={cn(s.bar, i <= curQuestion && s.active)} />
                ))}
        </div>
    )
}

// eslint-disable-next-line max-lines-per-function
function QuestionFlow({ onClose, setTab, card }: Props) {
    const intl = useIntl()
    const answerRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

    const [answer, setAnswer] = useState('')

    const { data: questionsData, mutate: mutateQuestions } = useGetThreeDSQuestionsData({ id: card?.id || '' })
    const { mutate: mutateConfig } = useGetThreeDSConfigData({ id: card?.id || '' })

    const questions = questionsData?.questions || []
    const nonAnsweredQuestions = questions.filter(q => !q.answered)
    const currentStep = questions.length - nonAnsweredQuestions.length
    const currentQuestion = nonAnsweredQuestions[0]

    const { send: onAnswer, isLoading: isAnswerLoading } = usePromise(async () => {
        const isLast = nonAnsweredQuestions.length >= questions.length - 1
        await cardsApi.postThreeDSKba({
            id: card?.id || '',
            question_id: currentQuestion?.id || '',
            answer,
        })
        await mutateConfig()
        await mutateQuestions()

        answerRef?.current?.focus()
        setAnswer('')

        if (isLast) {
            setTab(SettingsTabs.threeDSSecurity)
        }
    })

    return (
        <div className={s.wrap}>
            <div className={s.top}>
                <FormattedHTMLMessage {...lang.title} className={s.title} />
                <ProgressBar questionLen={questions.length} curQuestion={currentStep} />
            </div>
            <FormattedHTMLMessage
                {...lang.questionOf}
                className={s.qTitle}
                values={{ question: currentStep + 1, qLen: questions.length }}
            />
            <div className={s.content}>
                <Input
                    label={intl.formatMessage(lang.question)}
                    value={currentQuestion?.text || ''}
                    dataPointer="input1_question"
                    readOnly
                />
                <Input
                    label={intl.formatMessage(lang.your)}
                    value={answer}
                    onChange={setAnswer}
                    dataPointer="input2_answer"
                    autoFocus
                    inputRef={answerRef}
                />
            </div>
            <div className={s.button}>
                <Button dataPointer="setup" size="l" onClick={onClose} fullWidth>
                    <FormattedHTMLMessage {...lang.cancel} />
                </Button>
                <Button
                    dataPointer="setup"
                    size="l"
                    mode="primary"
                    onClick={onAnswer}
                    fullWidth
                    isLoading={isAnswerLoading}
                >
                    <FormattedHTMLMessage {...(currentStep === questions.length - 1 ? lang.submit : lang.nextQ)} />
                </Button>
            </div>
        </div>
    )
}

export default QuestionFlow
